import { CommonConstants, CountryLabels, LocaleLabels, TimezoneLabels } from '@shared/common';
import { SetStoredLanguage } from '@shared/frontend';
import type { LocalizationCode } from '@shared/common';

/** Localized strings for the package. */
export const Localized: Record<string, string> = {
  // Shared UI
  DashName: 'Vertex Echo',
  DashLoading: 'Loading...',
  // List search + pagination panel (shared by Calls/Leads/Bookings/Notifications/Invoices).
  ListPerPage: 'Per page',
  ListPrevious: 'Previous',
  ListNext: 'Next',
  CallsSearchPlaceholder: 'Search by number, summary, intent, or status',
  LeadsSearchPlaceholder: 'Search by name, phone, email, or reason',
  BookingsSearchPlaceholder: 'Search by customer, phone, notes, or status',
  NotificationsSearchPlaceholder: 'Search by recipient, channel, subject, or status',
  InvoicesSearchPlaceholder: 'Search by status or amount',
  DashSignOut: 'Sign out',
  DashSignOutHint: 'Sign out of your Vertex Echo account.',
  DashNavToggle: 'Open the navigation menu.',
  DashLanguage: 'Language',
  DashLanguageHint: 'Change the language used across your dashboard.',
  DashRetry: 'Retry',
  DashEmpty: 'Nothing here yet.',
  DashError: 'Something went wrong. Please try again.',
  DashBack: 'Back',
  DashSave: 'Save',
  DashSaving: 'Saving...',
  DashSaved: 'Saved.',
  // Shared — common field names and labels used in multiple sections.
  SharedBusinessName: 'Business name',
  SharedIndustry: 'Industry',
  SharedPlan: 'Plan',
  SharedEmail: 'Email',
  SharedStatus: 'Status',
  SharedCancel: 'Cancel',
  SharedClose: 'Close',
  SharedDone: 'Done',
  SharedActive: 'Active',
  SharedInactive: 'Inactive',
  SharedConnected: 'Connected',
  SharedPending: 'Pending',
  SharedInProgress: 'In progress',
  SharedResolved: 'Resolved',
  SharedPaid: 'Paid',
  SharedUnpaid: 'Unpaid',
  SharedRefresh: 'Refresh',
  SharedOpen: 'Open',
  SharedActions: 'Actions',
  SharedCollapse: 'Collapse',
  SharedExpand: 'Expand',
  SharedTo: 'To',
  SharedWhen: 'When',
  SharedUpdated: 'Updated',
  SharedAccount: 'Account',
  SharedContactEmail: 'Contact email',
  SharedCreateAccount: 'Create account',
  SharedContactPhone: 'Contact Phone',
  SharedTermsOfService: 'Terms of Service',
  SharedSmsNotice: 'SMS Notice',
  SharedBilling: 'Billing',
  SharedTryAgain: 'Try again',
  SharedSetUpBilling: 'Set up billing',
  SharedGetStarted: 'Get Started',
  SharedPerMonth: '/mo',
  SharedFree: 'FREE',
  SharedPhoneExample: '+14155550123',
  // Status badges — shared across every dashboard data table. Keyed by the server status value (the value is effectively the key); StatusLabel() resolves the raw value to the translated text below.
  StatusCompleted: 'Completed',
  StatusSent: 'Sent',
  StatusProcessed: 'Processed',
  StatusSuccess: 'Success',
  StatusSucceeded: 'Succeeded',
  StatusQueued: 'Queued',
  StatusProcessing: 'Processing',
  StatusTrialing: 'Trial',
  StatusGrace: 'Grace period',
  StatusFailed: 'Failed',
  StatusError: 'Error',
  StatusCanceled: 'Canceled',
  StatusPastDue: 'Past due',
  StatusBlocked: 'Blocked',
  StatusUnpaid: 'Unpaid',
  StatusWarning: 'Warning',
  StatusAttention: 'Attention',
  StatusNeedsReview: 'Needs review',
  StatusIncomplete: 'Incomplete',
  // Navigation
  NavOverview: 'Overview',
  NavCalls: 'Calls',
  NavTexts: 'Texts',
  NavLeads: 'Leads',
  NavBookings: 'Bookings',
  NavKnowledge: 'Knowledge',
  NavProfile: 'Business',
  NavIntegrations: 'Integrations',
  NavNotifications: 'Notifications',
  NavAccount: 'Account',
  NavBilling: 'Billing',
  NavInvoices: 'Invoices',
  // Auth / login
  AuthPassword: 'Password',
  AuthPasswordConfirm: 'Confirm password',
  AuthPasswordMismatch: 'Passwords do not match.',
  AuthSubmitSignIn: 'Sign in',
  AuthToSignUp: 'Need an account? Create one',
  AuthToSignIn: 'Have an account? Sign in',
  AuthWelcome: 'Welcome to Vertex Echo',
  AuthSubtitle: 'Manage your AI receptionist, calls, and bookings.',
  AuthError: 'Sign-in failed. Check your email and password.',
  AuthErrorEmailInUse: 'An account with this email already exists. Use another email or sign in.',
  AuthForgot: 'Forgot password?',
  AuthSendReset: 'Send reset link',
  AuthResetSent: 'Password reset email sent. Check your inbox.',
  AuthBackToSignIn: 'Back to sign in',
  AuthEmailRequired: 'Please enter your email address',
  AuthFieldsRequired: 'Please enter your email and password',
  AuthAgreePrefix: 'I agree to the',
  AuthAgreeRequired: 'Please review and accept the Terms of Service to create your account.',
  AuthSmsConsentPrefix: 'Optional - I agree to receive SMS messages as detailed in the',
  AuthTermsIntro:
    'Vertex Echo by SKAR AI provides an AI receptionist that answers your business line when you miss a call, books appointments, and captures caller details on your behalf. By creating an account you agree to these terms.',
  AuthTermsSmsTitle: 'SMS notifications and consent',
  AuthTermsSmsBody:
    'By checking the optional SMS consent box and providing your contact phone number, you choose to receive text messages (SMS) from Vertex Echo at that number. These are operational, non-marketing messages: missed-call summaries, captured lead details, appointment booking notifications, and urgent caller escalations. Message frequency varies with your call volume. Message and data rates may apply.',
  AuthTermsSmsOptOut: 'Reply STOP at any time to opt out of SMS notifications, or switch your notification channel to email in your dashboard Business Profile. Reply HELP for help.',
  AuthTermsCallersTitle: 'Messages to your callers',
  AuthTermsCallersBody:
    'When a caller asks for a follow-up during a call handled by your AI receptionist, Vertex Echo may send that caller a single text message on your behalf (for example, a booking confirmation or requested information). Callers consent verbally during the call, are messaged only at the number they called from, and can reply STOP to opt out.',
  AuthTermsPrivacyTitle: 'Privacy',
  AuthTermsPrivacyBody:
    'Your phone number and messaging consent are used solely to deliver the notifications described above. No mobile information will be shared with third parties or affiliates for marketing or promotional purposes. Call recordings, transcripts, and captured lead details are stored to power your dashboard and are accessible only to your business account.',
  AuthSmsNoticeIntro: 'This is an optional service. By checking the SMS consent box, you choose to receive text messages (SMS) from Vertex Echo at your contact phone number.',
  AuthNoTenant: 'Your account is not linked to a business yet. Contact your administrator.',
  // Onboarding (self-service tenant provisioning for accounts with no business yet).
  OnboardingTitle: 'Create your business',
  OnboardingSubtitle: 'Tell us about your business and we will set up your account, AI receptionist, and AI Number.',
  OnboardingBusinessNamePlaceholder: 'e.g. Summit Plumbing Co.',
  OnboardingContactName: 'Contact name',
  OnboardingContactNamePlaceholder: 'e.g. Jordan Smith',
  OnboardingContactPhone: 'Contact number',
  OnboardingNichePlaceholder: 'Select your industry',
  OnboardingPlanPlaceholder: 'Select a plan',
  OnboardingAreaCodes: 'Preferred AI area codes',
  OnboardingAreaCodesPlaceholder: 'e.g. 415, 510, 650',
  OnboardingCountry: 'Country',
  OnboardingCountryPlaceholder: 'Select your country',
  // Country labels — single source in @shared/common GetSupportedCountriesData so the lobby and portal stay identical. Keyed by GetSupportedCountriesData labelKey.
  ...CountryLabels,
  OnboardingTimezone: 'Timezone',
  OnboardingTimezonePlaceholder: 'Select your timezone',
  // Timezone labels — single source in @shared/common GetTimezoneData so the lobby and portal stay identical. Keyed by GetTimezoneData labelKey.
  ...TimezoneLabels,
  OnboardingSubmitting: 'Setting up your account...',
  OnboardingErrorRequired: 'All fields are required.',
  OnboardingErrorPhone: 'Enter a valid contact number in international format, e.g. +14155550123.',
  OnboardingError: 'We could not finish setting up your account. Please try again later or contact {0}.',
  OnboardingLoadError: 'We could not load the onboarding options. Please retry.',
  // Onboarding wizard: the persistent checklist and the phone step gate the whole dashboard until every step succeeds (fail-closed, resumable at the exact step the owner left off).
  OnboardingStepAccountName: 'Business details',
  OnboardingStepAccountHint: 'Your company, industry, and plan',
  OnboardingStepBillingHint: 'Start your subscription',
  OnboardingStepPhoneName: 'AI Number',
  OnboardingStepPhoneHint: 'Activate your AI Receptionist Phone Number',
  OnboardingStatusFailed: 'Action needed',
  OnboardingPhoneTitle: 'Activate your AI Number',
  OnboardingPhoneSubtitle: 'We are reserving your AI Receptionist Phone Number — the dedicated AI Number your clients call. This only takes a moment.',
  OnboardingPhoneProvisioning: 'Setting up your AI Number...',
  OnboardingPhoneError: 'We could not set up your AI Number. Please try again or contact {0}.',
  // Twilio preferred-number picker (shown when the voice provider returns candidates instead of allocating a number outright).
  OnboardingPhonePickTitle: 'Choose your AI Number',
  OnboardingPhonePickSubtitle: 'Pick up to two preferred numbers. We try them in order, and fall back to the next best available number if they are taken.',
  OnboardingPhonePickEmpty: 'No numbers are available for your area right now. Please try again or contact {0}.',
  OnboardingPhonePickContinue: 'Continue',
  OnboardingPhonePickContinueError: 'We could not reserve your selected number. Please try again or contact {0}.',
  OnboardingLoadFailed: 'We could not load your onboarding progress. Please try again or contact {0}.',
  // Billing setup gate (shown after onboarding until the tenant completes hosted Stripe checkout — trial + selected plan).
  SetupBillingSubtitle: 'Before your dashboard opens, we need to set up billing to verify you are human. You are not charged until your trial period is over, and you can cancel at any time.',
  SetupBillingPlanLabel: 'Selected plan',
  SetupBillingPlanTrial: '{0}-day free trial',
  SetupBillingPlanPrice: '{0} per month for base service; overage charges apply only for heavy usage',
  SetupBillingPlanCancel: 'Cancel before the trial ends and no charges will apply',
  SetupBillingRedirecting: 'Redirecting to secure checkout...',
  SetupBillingFinalizing: 'Finalizing your billing setup...',
  SetupBillingCancelled: 'Billing setup was cancelled. You can set it up whenever you are ready.',
  SetupBillingError: 'We could not start billing setup. Please try again later or contact {0}.',
  SetupBillingTimeout: 'Your billing setup is still finalizing. Please wait a moment and refresh, or contact {0} if it does not complete.',
  // Overview
  OverviewTitle: 'Overview',
  OverviewWelcome: 'Welcome back, {0}',
  OverviewCalls: 'Recent calls',
  OverviewCallsHint: 'Calls answered by your AI receptionist in the last 7 days. Click to see all calls.',
  OverviewTexts: 'Recent texts',
  OverviewTextsHint: 'Text conversations with customer activity in the last 7 days. Click to see all texts.',
  OverviewLeadsHint: 'People who reached out and are still waiting for a follow-up. Click to see them.',
  OverviewBookingsHint: 'Appointments that are still coming up. Click to see all bookings.',
  OverviewServiceStatusHint: 'Whether your AI receptionist is answering calls right now. Click to manage billing.',
  OverviewLeads: 'Open leads',
  OverviewBookings: 'Bookings',
  OverviewServiceStatus: 'Service status',
  OverviewAIOn: 'AI answering is on',
  OverviewAIOff: 'AI answering is off',
  OverviewAINumber: 'AI answering {0}',
  OverviewPaymentOverdue: 'Payment overdue',
  OverviewPaymentOverdueHint: 'Update your billing to keep your AI active.',
  OverviewNextStep: 'Next Step',
  OverviewAddCallInstructions: 'Add Call Instructions',
  OverviewAddCallInstructionsHint: 'Tell your AI receptionist how to handle your calls.',
  OverviewAddKnowledge: 'Add Business Knowledge',
  OverviewAddKnowledgeHint: 'Upload documents so your AI receptionist can answer caller questions.',
  // Calls
  CallsTitle: 'Calls',
  CallsTitleAt: 'Calls at {0}',
  CallsFrom: 'From',
  CallsDuration: 'Duration',
  CallsStatusHint:
    'Where this call is in its lifecycle. "In progress" means the call is still happening right now; it changes to "Completed" as soon as the caller hangs up. A completed call is final - it never changes again.',
  CallsIntent: 'Intent',
  CallsIntentHint:
    'What the caller wanted, worked out automatically from the conversation - for example booking an appointment, asking a question, or requesting a callback. It appears shortly after the call ends; a dash means the call is still being processed or was too short to tell.',
  CallsSummary: 'Summary',
  CallsSummaryHint:
    'A two-sentence recap of the call, written automatically after it ends - the same text we send you in your missed-call alert. It appears within a minute or two of the call ending.',
  CallsNone: 'No calls yet.',
  CallDetailTitle: 'Call details',
  CallPanelTitle: 'Call',
  CallTranscript: 'Transcript',
  CallTranscriptHint: 'The written record of the conversation, speaker by speaker. It appears shortly after the call ends; a very short call (a quick hang-up) may have nothing to show.',
  CallTranscriptNone: 'No transcript available.',
  CallRecording: 'Recording',
  CallRecordingHint: 'The audio of the call, when one was captured. Very short calls, or calls that never connected, may have no recording.',
  CallRecordingNone: 'No recording available.',
  CallRecordingExpired: 'Recording has expired.',
  CallOutcome: 'Outcome',
  CallOutcomeHint:
    'What came out of the call once it was reviewed automatically: the caller\u2019s intent, their contact details when shared, and any follow-up captured for you. It appears within a minute or two of the call ending - "Needs review" means we could not work it out confidently and it is worth a quick look.',
  CallOutcomeNone: 'No outcome recorded.',
  CallExtracted: 'Extracted data',
  CallResult: 'Result',
  CallMarkResolved: 'Mark resolved',
  CallSpeakerCaller: 'Caller',
  CallSpeakerAgent: 'Agent',
  // Texts (AI texting conversations)
  TextsTitle: 'Texts',
  TextsSearchPlaceholder: 'Search by customer number',
  TextsCustomer: 'Customer',
  TextsLastMessage: 'Last message',
  TextsLastActivity: 'Last activity',
  TextsNone: 'No texts yet.',
  TextsOptedOut: 'Opted out',
  TextsOptedOutHint: 'This customer replied STOP, so no new messages can be sent to them.',
  TextDetailTitle: 'Conversation',
  TextTranscriptNone: 'No messages yet.',
  TextSenderCustomer: 'Customer',
  TextSenderAI: 'AI',
  TextSenderYou: 'You',
  TextSenderSystem: 'System',
  TextSendPlaceholder: 'Write a message to this customer...',
  TextSend: 'Send',
  TextSendBusy: 'Sending...',
  TextSendFailed: 'The message could not be sent. Please try again.',
  // Knowledge
  KnowledgeTitle: 'Knowledge',
  KnowledgeDocuments: 'Documents',
  KnowledgeUpload: 'Upload',
  KnowledgeUploading: 'Uploading...',
  KnowledgeCreate: 'Create',
  KnowledgeNewTitle: 'New knowledge',
  KnowledgeEditTitle: 'Edit knowledge',
  KnowledgeName: 'Name',
  KnowledgeNamePlaceholder: 'A short title for this knowledge',
  KnowledgeBody: 'Body',
  KnowledgeBodyPlaceholder: 'Type the information you want your assistant to know.',
  KnowledgeSave: 'Save',
  KnowledgeSaving: 'Saving...',
  KnowledgeCreateError: 'We could not create that knowledge. Please try again.',
  KnowledgeUpdateError: 'We could not update that knowledge. Please try again.',
  KnowledgeLoadError: 'We could not load that knowledge. Please try again.',
  KnowledgeDocTitle: 'Title',
  KnowledgeDelete: 'Delete',
  KnowledgeNone: 'No documents yet. Upload one to teach your assistant.',
  KnowledgeDeleteConfirm: 'Delete this document?',
  KnowledgeUploadError: 'We could not upload that document. Please try again.',
  KnowledgeUploadInvalid: 'Only .txt and .pdf files up to 10 MB can be uploaded.',
  KnowledgeUploadSomeSkipped: 'Some files were skipped. Only .txt and .pdf files up to 10 MB can be uploaded.',
  KnowledgeLimitReached: 'You can store up to {0} knowledge documents. Delete one before adding more.',
  KnowledgeDeleteError: 'We could not delete that document. Please try again.',
  KnowledgeEditHint: 'Edit this knowledge.',
  KnowledgeHelpTitle: 'How your knowledge base works',
  KnowledgeHelpIntro:
    'Your knowledge base teaches your AI receptionist about your business - the services you offer, prices, policies, service areas, hours, and the questions callers ask most - so it can answer accurately instead of guessing.',
  KnowledgeHelpFormats:
    'You can upload plain-text (.txt) and PDF (.pdf) files up to 10 MB each, with as many documents as you need. Plain text works best: a clear, typed document with short headings and a question-and-answer layout is the easiest for the assistant to read. Text-based PDFs are fine too; scanned or photographed pages, image-only files, and complex multi-column layouts work poorly.',
  KnowledgeHelpManage:
    'You do not have to upload a file - you can also create a document by typing it in directly, and you can open any document later to edit it or delete it. After you add or change a document it needs a short moment to process before the assistant can use it; the Status column shows when it is ready, and Refresh re-checks it. Once a document is ready, the very next call uses it - there is no retraining and no waiting period.',
  KnowledgeHelpOrganize:
    'Keep each document focused on one topic (for example: pricing, booking policy, or FAQs), write the way you would answer a customer, and remove anything out of date so the assistant never repeats stale information.',
  KnowledgeHelpUsage:
    'When a caller asks a question, the assistant looks through your documents for the most relevant passages and uses them to shape its reply. The more clearly your information is written, the better its answers.',
  KnowledgeHelpHours:
    'Your operating hours on the Business page are the regular weekly bounds the booking system enforces. Use the knowledge base for the finer detail those hours cannot capture: holidays, temporary closures, seasonal changes, and mid-day breaks. Note them clearly (for example: "Closed December 25" or "Closed for lunch 12:00 to 1:00") so the assistant checks them before confirming any appointment.',
  // Business profile
  ProfileTitle: 'Business',
  ProfilePanelTitle: 'Business profile',
  ProfileDetails: 'Details',
  ProfileLegalName: 'Legal name',
  ProfileLegalNameHint: 'Your registered legal/business entity name, if different from the name customers know you by. Used on invoices and formal records.',
  ProfileWebsite: 'Website',
  ProfileWebsitePlaceholder: 'yourwebsite.com',
  ProfileAddress: 'Address',
  ProfileAddressLine1: 'Address line 1',
  ProfileAddressLine2: 'Address line 2',
  ProfileCity: 'City',
  ProfileRegion: 'Region',
  ProfilePostal: 'Zip/Postal code',
  ProfileCountry: 'Country',
  ProfileGreeting: 'Agent greeting',
  ProfileTone: 'Agent tone',
  ProfileAgentInstructions: 'Call-handling instructions',
  ProfileVoice: 'Agent voice',
  ProfileLeadsLabel: 'Leads term',
  ProfileLeadsLabelHint: 'e.g. Leads, Visitors',
  ProfileBookingsLabel: 'Bookings term',
  ProfileBookingsLabelHint: 'e.g. Bookings, Appointments',
  ProfileVoiceFemale: 'Female',
  ProfileVoiceFemaleBright: 'Female - Bright',
  ProfileVoiceFemaleDeep: 'Female - Deep',
  ProfileVoiceMale: 'Male',
  ProfileVoiceMaleDeep: 'Male - Deep',
  ProfileVoiceMaleBright: 'Male - Bright',
  ProfileNotifyChannel: 'Notification Channel',
  ProfileConcurrentLimit: 'Simultaneous call limit',
  ProfileNotifyChannelSMS: 'Contact Phone (SMS)',
  ProfileNotifyChannelEmail: 'Contact Email',
  ProfileHours: 'Operating hours',
  ProfileServices: 'Services',
  ProfileServicesHint: 'The services your business offers. The assistant shares them with callers and books the right amount of time for each.',
  ProfileServicesNone: 'No services set',
  ProfileServiceNamePlaceholder: 'Name, e.g. Water heater installation',
  ProfileServiceDescriptionPlaceholder: 'Optional one-line description the assistant can share with callers.',
  ProfileServiceDuration: 'Minutes',
  ProfileServiceAdd: 'Add service',
  ProfileServiceRemove: 'Remove',
  ProfileAddressNone: 'No address set',
  ProfileHoursNone: 'No operating hours set',
  ProfileHoursOpens: 'Opens',
  ProfileHoursCloses: 'Closes',
  ProfileDay0: 'Sunday',
  ProfileDay1: 'Monday',
  ProfileDay2: 'Tuesday',
  ProfileDay3: 'Wednesday',
  ProfileDay4: 'Thursday',
  ProfileDay5: 'Friday',
  ProfileDay6: 'Saturday',
  ProfileSaved: 'Business profile saved.',
  ProfileSave: 'Save Updates',
  // Partial save: the profile write succeeded but the business-settings write failed; {0} is the server's reason. The form re-syncs to the persisted state so a re-save is a clean retry.
  ProfileSavePartial: 'Your business details were saved, but the name, industry, timezone, or language settings could not be saved: {0}. Review and save again.',
  ProfileNone: 'No business profile yet. Add your details below.',
  ProfileErrorRequired: 'Please fill in all required fields.',
  ProfileErrorEmail: 'Please enter a valid contact email address (like name@business.com).',
  ProfileErrorURL: 'Please enter a valid website address (like www.business.com).',
  AccountErrorPhone: 'Please enter the contact phone in international format (like +15551234567).',
  AccountErrorEmail: 'Please enter a valid contact email address (like name@business.com).',
  ProfileRegionHint: 'State, province, or region in your business address. Example: California or CA.',
  ProfilePostalHint: 'Postal or ZIP code for your business address. Example: 94105.',
  ProfileCountryHint: 'Country where your business operates. Example: United States or US.',
  ProfileCountryLocked: 'Country is set at signup and cannot be changed here because it determines your billing currency. Contact support if it must be updated.',
  ProfileGreetingHint: 'The first line your AI receptionist says when it answers a call. Example: "Thanks for calling Summit Plumbing, how can I help?"',
  ProfileAgentInstructionsHint:
    'Optional instructions for how your AI receptionist should run its calls: what to ask first, what to emphasize, which next steps to offer, and what to always collect. Example: "Ask what kind of work they need before quoting. Always collect a callback number and email. Mention our seasonal tune-up special when relevant."',
  ProfileAgentInstructionsHelp: 'Ask Google AI Mode for call-handling instruction ideas tailored to your business. Opens in a new tab.',
  ProfileAgentInstructionsHelpPrompt:
    "What are the best call-handling instructions for an AI receptionist at {0}, a {1} business? Focus on the most important, most common caller questions and requests - these instructions cover day-to-day call handling, not the business's full knowledge base. Cover what to ask first, what to emphasize, which next steps to offer, and what to always collect from the caller. Keep the whole answer under {2} characters so it can be pasted straight into the instructions field.",
  ProfileAgentInstructionsHelpNameFallback: 'my business',
  ProfileAgentInstructionsHelpNicheFallback: 'local service',
  ProfileToneHint:
    'The personality your AI receptionist uses with callers, in your own words. Examples: "Friendly and upbeat", "Calm, professional, and reassuring", "Casual and quick - get to the point", "Warm but formal, like a luxury concierge".',
  ProfileVoiceHint: 'The voice callers hear when your AI receptionist speaks. Your choice applies from the very next call.',
  ProfileNotifyChannelHint: 'How we send you notifications - missed-call summaries, new leads, and urgent escalations. Example: Text message (SMS) or Email.',
  // Business profile help panel
  ProfileHelpTitle: 'How your business profile works',
  ProfileHelpIntro:
    'This page holds everything your AI receptionist needs to represent you: your business profile, your sign-in details, and the contact we notify about calls. Your name, industry, hours, and contact details shape how it greets and answers callers, so keep them accurate.',
  ProfileHelpBasics:
    'Business name, website, industry, address, and services are the core facts your assistant can share with callers. Your industry also loads a starter knowledge base for your trade, and for some industries it lets you name your own words for leads and bookings.',
  ProfileHelpServices:
    'Services list what you offer, each with an optional one-line description and a typical duration in minutes. The assistant uses them to answer "what do you do?", ask which service a caller needs, and reserve the right amount of calendar time when booking. A service without a duration books a standard-length appointment, and with no services listed the assistant still books normally - but listing them makes its answers and bookings noticeably sharper.',
  ProfileHelpRegion:
    'Timezone keeps the assistant in step with you: every appointment time and every "today" or "tomorrow" a caller mentions is worked out in your timezone, and booking confirmations state times in it.',
  ProfileHelpLanguage:
    'Default language is the language your business runs in, and it reaches further than the call itself. The assistant opens and holds every call in it - it still understands callers who speak something else and switches only when a caller asks - and the speaking voice is chosen to pronounce it well. Your missed-call alerts and booking notifications arrive written in it, including the call summary itself, and the texts we send your callers (like booking confirmations) are in it too. Two things it does not change: call transcripts always show the conversation exactly as it was spoken, and the dashboard\u2019s own display language is your personal choice in the language menu.',
  ProfileHelpAccount:
    'Account details holds your sign-in username, login email, and password. Contact details holds the owner name, phone, and email we send missed-call alerts, call summaries, and booking requests to. Keep both current.',
  ProfileHelpGreeting:
    'Agent greeting is the exact first line callers hear when the AI answers - for example "Thanks for calling Summit Plumbing, how can I help?". Leave it blank for a standard greeting.',
  ProfileHelpVoice:
    'Agent voice is the voice callers hear. Each gender has a default, a Bright (higher-energy, upbeat) variant, and a Deep (lower, calmer) variant. Your choice applies from the next call.',
  ProfileHelpTone:
    'Agent tone sets the personality the assistant uses with callers, in your own words. Examples: "Friendly and upbeat", "Calm, professional, and reassuring", "Casual and quick - get to the point", "Warm but formal, like a luxury concierge".',
  ProfileHelpTerms:
    'For some industries you can set your own words for leads and bookings (for example "Reservations" or "Consultations"). When set, the assistant uses your word with callers and your dashboard shows it too. Most industries use their standard terms, so this option only appears when it applies.',
  ProfileHelpInstructions:
    'Call-handling instructions are your own playbook for the assistant: what to ask early in the call, what to emphasize, which next steps to offer, and what it should always collect before hanging up. The assistant follows them on every call - but they never override its safety rules.',
  ProfileHelpAlerts:
    'Notification Channel sets how we reach you after each call: Contact Phone (SMS) or Contact Email. Call summaries, new leads, and booking requests all arrive on that channel, sent to the phone and email in the Contact details panel on this page.',
  ProfileHelpMessages:
    'One message ignores that setting: any follow-up the assistant sends a caller always goes by text to the caller\u2019s own number. Urgent escalations arrive on the channel you choose here, like every other notification. You can also reply STOP to any text to switch this setting to email, and START to switch back.',
  ProfileHelpHours:
    'Operating hours are your regular day-to-day open and close times, and the assistant enforces them: it will not book an appointment outside them. Keep them set to your normal weekly schedule. For anything those weekly hours cannot express - holidays, temporary closures, seasonal changes, or mid-day breaks - add a note in your knowledge base, and the assistant will check it before confirming a time.',
  // Integrations
  IntegrationsTitle: 'Calendar integrations',
  IntegrationsProvider: 'Provider',
  IntegrationsCalendar: 'Calendar',
  IntegrationsCalendarDefault: 'Default',
  IntegrationsNone: 'No integrations connected yet.',
  IntegrationsConnectTitle: 'Connect a calendar',
  IntegrationsConnectIntro: 'Connect a scheduling provider so the assistant can read availability and book appointments.',
  IntegrationsConnectAction: 'Connect',
  IntegrationsConnecting: 'Connecting…',
  IntegrationsChooseResource: 'Choose a calendar',
  IntegrationsResourcePlaceholder: 'Select…',
  IntegrationsActivate: 'Activate',
  IntegrationsActivating: 'Activating…',
  IntegrationsDisconnect: 'Disconnect',
  IntegrationsDisconnecting: 'Disconnecting…',
  IntegrationsActiveBadge: 'Active for bookings',
  IntegrationsMakeActive: 'Use for bookings',
  IntegrationsMakingActive: 'Switching…',
  IntegrationsConfirmActive: 'Switch bookings to this calendar? Your current calendar stays connected, but new appointments will go to the new one. Existing appointments are not moved.',
  IntegrationsConfirmActiveAction: 'Switch',
  IntegrationsActions: 'Actions',
  IntegrationsConnectedNotice: 'Provider connected. Choose which calendar or service the assistant should use.',
  IntegrationsConnectError: 'We could not finish connecting that provider. Please try again.',
  IntegrationsReconnect: 'Reconnect',
  IntegrationsLoadingResources: 'Loading…',
  IntegrationsProviderGoogleCalendar: 'Google Calendar',
  IntegrationsProviderMicrosoftOutlook: 'Microsoft Outlook',
  IntegrationsConnectHint: 'Link a scheduling provider so your AI receptionist can read your real availability and book appointments straight onto your calendar. Example: Google Calendar or Outlook.',
  IntegrationsListHint: 'Every scheduling tool you have connected. The one marked "Active for bookings" receives the appointments your assistant books.',
  // Notifications
  NotificationsTitle: 'Notification messages',
  NotificationsHint:
    'Alerts your AI receptionist sends you after each call — new leads, booking confirmations, and follow-ups. They arrive by text or email at the contact details on your Business Profile, per its Notify Channel setting.',
  NotificationsChannel: 'Channel',
  NotificationsBody: 'Message',
  NotificationsResend: 'Resend',
  NotificationsResendDone: 'Notification re-sent.',
  NotificationsNone: 'No notifications sent yet.',
  // Leads
  LeadsTitle: 'Leads',
  LeadsPanelTitle: 'Leads list',
  PanelListSuffix: '{0} list',
  LeadsHint: 'Callers who left contact details for follow-up. Resolve one once you have reached back out; it then drops out of your Overview open-leads count.',
  LeadsNone: 'No leads captured yet.',
  LeadsReason: 'Reason',
  LeadsCallback: 'Preferred callback',
  LeadsResolve: 'Resolve',
  LeadsResolved: 'Lead resolved.',
  // Bookings
  BookingsTitle: 'Bookings',
  BookingsPanelTitle: 'Bookings list',
  BookingsHint: 'Appointments your assistant booked. They live on your connected calendar, so manage and complete them there.',
  BookingsNone: 'No bookings yet.',
  BookingsWhen: 'When',
  BookingsNotes: 'Notes',
  // Account (Help / Account / Business / Owner panels)
  AccountEdit: 'Edit',
  AccountDetailsTitle: 'Account details',
  // Help panel
  AccountHelpTitle: 'How your account works',
  AccountHelpIntro: 'This is where you manage your sign-in details, your business information, and the owner we contact about your calls.',
  AccountHelpData:
    'Your business details shape how your AI receptionist greets and helps callers. Your Contact Phone and Contact email - in the Contact panel below - are where we send missed-call alerts, call summaries, and booking requests, so keep them up to date.',
  AccountHelpAlerts: 'Choose whether those alerts reach you by text or email on the Business profile page, under "Notification Channel."',
  AccountHelpCalendar:
    'Want bookings added straight to your calendar? Connect a scheduling provider on the Integrations page. Until then nothing is booked automatically - the assistant simply sends each booking to you as a request so you can confirm it yourself.',
  AccountHelpContact: 'Need a hand? Contact {0} and our team will be glad to help.',
  // Account panel (sign-in details)
  AccountUsername: 'Username',
  AccountEmailStatus: 'Email verification',
  AccountVerified: 'Verified',
  AccountResend: 'Resend verification',
  AccountChangePassword: 'Change password',
  AccountChangePasswordHint: 'Leave these blank to keep your current password.',
  AccountCurrentPassword: 'Current password',
  AccountNewPassword: 'New password',
  AccountConfirmPassword: 'Confirm new password',
  AccountPasswordMismatch: 'The new password and confirmation do not match.',
  AccountSaveError: 'We could not save your changes. Please check your details and try again.',
  AccountBusinessErrorRequired: 'Please fill in all required fields.',
  // Business panel (tenant)
  AccountBusiness: 'Business',
  AccountTimezone: 'Timezone',
  AccountLocale: 'Agent language',
  AccountLocaleHint:
    'The language your AI agent speaks on calls. The assistant opens and holds calls in it (switching only when a caller asks), your alerts and call summaries are written in it, and caller texts like booking confirmations use it too. Only languages the agent can reliably understand are listed here. Transcripts and the dashboard display language are not affected.',
  // Owner panel (tenant user)
  AccountOwner: 'Contact details',
  AccountDisplayName: 'Name',
  AccountRole: 'Role',
  AccountRoleAdmin: 'Admin',
  AccountRoleEmployee: 'Staff',
  // Billing
  BillingPeriod: 'Current period',
  BillingPeriodHint:
    'Your current billing cycle. Usage shown on this page accumulates within this window and resets when the cycle renews — monthly, on the day you subscribed. During a trial the cycle runs from signup to the trial\u2019s end.',
  BillingPendingChange: 'Pending change',
  BillingCancelsOn: 'Cancels on',
  BillingUsage: 'Usage this period',
  BillingInvoices: 'Invoices',
  InvoicesTitle: 'Invoices',
  // Current-plan details panel: the plan the tenant is on plus its pricing in the subscription's settlement currency.
  BillingPlanTitle: 'Plan details',
  BillingPlanCurrent: 'Current Plan',
  BillingDetailsTitle: 'Billing details',
  BillingPlanMonthly: 'Monthly',
  BillingPlanSetupFee: 'One-time setup fee',
  BillingPlanIncluded: 'Included with your plan',
  BillingPlanNextMonth: 'Plan starting next month',
  BillingPlanUnavailable: 'Plan details are unavailable.',
  BillingInvoiceAmount: 'Amount',
  BillingInvoiceDate: 'Date',
  BillingViewInvoice: 'View',
  BillingManage: 'Manage billing',
  BillingNoSubscription: 'No active subscription.',
  BillingNoInvoices: 'No invoices yet.',
  BillingMeter: 'Meter',
  BillingQuantity: 'Quantity',
  // Friendly labels for the usage meter keys shown in the usage table (the messages meter covers every message - owner notifications on any channel and customer texts in both directions).
  BillingMeterAnsweredCalls: 'Answered calls',
  BillingMeterVoiceMinutes: 'Voice minutes',
  BillingMeterNotifications: 'Messages',
  BillingAccessOk: 'Your subscription is active.',
  // Coupon discount blurb appended to the active-subscription line; {0} is the whole-number percent (e.g. "Getting 10% Off your plan every month!").
  BillingCouponBlurb: 'Getting {0}% Off your plan every month!',
  BillingAccessGrace: 'A payment failed. Your AI keeps answering during a short grace period — open Manage billing to update your card or pay the outstanding invoice.',
  BillingAccessGraceUntil: 'AI service pauses on',
  BillingAccessBlocked: 'A payment failed and your AI is paused. Open Manage billing to pay the outstanding invoice or update your card.',
  BillingRefreshStatus: 'Refresh status',
  BillingRefreshing: 'Checking…',
  BillingChangePlan: 'Change plan',
  BillingPlanNone: 'None — cancel subscription',
  BillingApply: 'Apply',
  BillingConfirmAction: 'Confirm',
  BillingConfirmSwitch: 'Switch to {0}? The change applies immediately and the unused part of this billing cycle is prorated automatically.',
  BillingConfirmResume: 'Keep {0}? The scheduled cancellation is removed and your subscription continues as normal.',
  BillingConfirmCancelPlan: 'Cancel your subscription? Your AI keeps answering until {0}, then service ends. You will not be charged again after this billing cycle.',
  BillingConfirmResubscribe: 'Re-subscribe on {0}? You will be redirected to secure checkout.',
  BillingPlanChanged: 'Plan updated.',
  BillingResumed: 'Cancellation removed — your subscription continues.',
  BillingCancelScheduled: 'Cancellation scheduled — service ends {0}.',

  // Landing page (public marketing site).
  // Header
  LandingLogoAlt: 'Vertex Echo',
  LandingNavLogin: 'Login',
  LandingNavDashboard: 'Dashboard',
  // Card 1 — Hero
  LandingHeroEyebrowStatic: 'AI Receptionist for',
  LandingHeroHeadline1: 'Every',
  LandingHeroHeadline2: 'Missed Call',
  LandingHeroHeadline3: 'Is A',
  LandingHeroHeadline4: 'Lost Job.',
  LandingHeroSub:
    "{0}14 DAY FREE TRIAL!{0}\n{2}Vertex Echo{2} answers your phone {1}24/7{1}, qualifies the lead, books the appointment, and texts a follow-up — all while you're on the job site. Never lose a contract to voicemail again.",
  LandingHeroCTA: 'Calculate Your Loss',
  LandingHeroStat1Value: '+27%',
  LandingHeroStat1Label: 'Bookings',
  LandingHeroStat1Title: '27% More Booked Jobs',

  LandingHeroStat2Value: '<1s',
  LandingHeroStat2Label: 'Latency',
  LandingHeroStat2Title: 'Less Than 1 Second Answer Latency',

  LandingHeroStat3Value: '24/7',
  LandingHeroStat3Label: 'Assistant',
  LandingHeroStat3Title: '24/7 Always On',

  LandingHeroStat4Value: '$0',
  LandingHeroStat4Label: 'Shortfall',
  LandingHeroStat4Title: '$0 Lost To Voicemail',
  // Card 2 — Demo (live call)
  LandingDemoLabel: 'Live Demo',
  LandingDemoTitle1: 'Hear The Lead',
  LandingDemoTitle2: 'You Just Missed.',
  LandingDemoSub: 'Call our live demo line and hear exactly how Echo answers, qualifies the job and books it — in real time, no scripts.',
  LandingDemoAgentRole: 'AI Receptionist',
  LandingDemoStatusLive: 'Demo line live now',
  LandingDemoStatusOffline: 'Demo line not live yet',
  LandingDemoCheck1: 'Answers in under one second',
  LandingDemoCheck2: 'Qualifies the job on the call',
  LandingDemoCheck3: 'Books straight to your calendar',
  LandingDemoCheck4: 'Texts a confirmation instantly',
  LandingDemoTagsLabel: 'Trained for trades like yours',
  LandingDemoCallPrefix: 'Call ',
  LandingDemoTextPrefix: 'Text ',
  LandingDemoCallDisabled: 'Try Later',
  LandingDemoCaption: 'Standard call or text rates apply · Real AI, no recordings',
  // Card 3 — Pain
  LandingPainLabel: 'The Problem',
  LandingPainTitle1: "You're Great At Your Trade.",
  LandingPainTitle2: "Your Phone Isn't.",
  LandingPainStatement: "You're 30 feet up finishing a ridge cap. Your phone rings. You can't answer. That was a $12,000 job.",
  LandingPainItem1Title: '20–40% of calls go unanswered',
  LandingPainItem1Desc: 'Trades owners miss nearly half their inbound leads just by being on the job.',
  LandingPainItem2Title: 'Leads go cold in under 5 minutes',
  LandingPainItem2Desc: "If you don't respond fast, they've already called your competitor.",
  LandingPainItem3Title: 'One missed roofing job = $6,000–$15,000',
  LandingPainItem3Desc: 'Your customer acquisition cost just went to zero return.',
  LandingPainItem4Title: 'After-hours calls are dead revenue',
  LandingPainItem4Desc: 'Emergency HVAC calls at 11pm go straight to voicemail. Your competitor answers.',
  // Card 4 — How it works
  LandingHowLabel: 'How It Works',
  LandingHowTitle1: 'Echo Handles It.',
  LandingHowTitle2: 'You Just Show Up.',
  LandingHowStep1Num: '01',
  LandingHowStep1Title: 'Echo Answers Instantly',
  LandingHowStep1Desc: 'Your dedicated Echo number picks up every call in under 1 second — day, night, weekends. Sounds human, stays on brand, never puts anyone on hold.',
  LandingHowStep2Num: '02',
  LandingHowStep2Title: 'Qualifies The Lead',
  LandingHowStep2Desc: 'Echo asks the right questions — job type, location, timeline, budget — and only books serious leads into your calendar. No tire-kickers, no wasted site visits.',
  LandingHowStep3Num: '03',
  LandingHowStep3Title: 'Books The Appointment',
  LandingHowStep3Desc: 'Echo checks your live availability and locks in the booking directly to your Google Calendar or Outlook. No back-and-forth. No double bookings.',
  LandingHowStep4Num: '04',
  LandingHowStep4Title: 'SMS Follow-Up Sent',
  LandingHowStep4Desc: 'Immediately after every call, Echo texts the customer a booking confirmation and your business details. The lead stays warm. You stay professional.',
  LandingHowStep5Num: '05',
  LandingHowStep5Title: 'You Get The Report',
  LandingHowStep5Desc: 'Every week you receive a summary — calls answered, leads qualified, jobs booked, revenue recovered. Your ROI is visible every single month.',
  LandingHowStep6Num: '06',
  LandingHowStep6Title: 'Live Instantly!',
  LandingHowStep6Desc:
    'Upload your business info, set your availability, get your dedicated number. Echo is live and answering calls as soon as you upload your information. No tech knowledge needed.',
  // Card 5 — ROI calculator
  LandingRoiLabel: 'ROI Calculator',
  LandingRoiTitle1: 'See Exactly What',
  LandingRoiTitle2: "You're Losing.",
  LandingRoiTradeLabel: 'Your Trade / Industry',
  LandingRoiJobValueLabel: 'Average Job Value',
  LandingRoiCallsLabel: 'Missed Calls Per Week',
  LandingRoiCallsUnit: 'calls / week',
  LandingRoiCloseLabel: 'Your Close Rate on Answered Calls',
  LandingRoiCloseUnit: '% close rate',
  LandingRoiOutputTop: 'Live Calculation',
  LandingRoiAnnualLossLabel: "Annual Revenue You're Losing",
  LandingRoiRowJobValue: 'Avg. job value for your trade',
  LandingRoiRowMonthlyCalls: 'Missed calls per month',
  LandingRoiRowJobsLost: 'Estimated jobs lost monthly',
  LandingRoiRowMonthlyLoss: 'Revenue lost monthly',
  LandingRoiRowEchoCost: 'Vertex Echo {0} cost',
  LandingRoiRowNetRecovery: 'Your monthly net recovery',
  LandingRoiCTA: 'Recover This Revenue',
  LandingRoiNote: 'Setup in minutes · No contracts · Cancel anytime',
  LandingRoiJobsUnit: 'jobs',
  LandingRoiCallsUnitShort: 'calls',
  LandingRoiNetEven: 'Even on 1 job',
  LandingRoiAvgSuffix: 'avg',
  // Card 6 — Pricing
  LandingPricingLabel: 'Pricing',
  LandingPricingTitle1: 'Simple Plans.',
  LandingPricingTitle2: 'Serious Returns.',
  LandingPricingCurrency: '$',
  LandingPricingPerMonth: 'per month + {0} setup. Prices in {1}.',
  LandingPricingPerMonthNoSetup: 'per month. Prices in {0}.',
  LandingPlanProBadge: 'Popular',
  LandingPlanCTA: 'Get Started',
  LandingPlanIncludedCalls: '{0} calls / {1} overage',
  LandingPlanIncludedMinutes: '{0} minutes / {1} overage',
  LandingPlanIncludedSMS: '{0} messages / {1} overage',
  LandingPlanConcurrentLines: '{0} concurrent lines',
  LandingPricingNote: 'Prices subject to change. All plans include a 14-day free trial. No contracts. Cancel anytime. Custom packages available.',
  // Card 7 — Signup
  LandingSignupTitle1: 'Your Number Is',
  LandingSignupTitle2: 'Ready.',
  // Shown in place of the activation form when the visitor is already authenticated (the onboarding "ready" panel CTA).
  LandingReadyContinue: 'Continue to Dashboard',
  LandingSignupFormTitle: 'Activate Vertex Echo',
  LandingSignupFormSub: 'Fill in your details and go live instantly!',
  LandingSignupContactName: 'Contact Name',
  LandingSignupContactNamePlaceholder: 'John Smith',
  LandingSignupBusiness: 'Business Name',
  LandingSignupBusinessPlaceholder: 'Smith Roofing Co.',
  LandingSignupAreaCodes: 'Preferred Area Codes',
  LandingSignupAreaCodesPlaceholder: 'e.g. 416, 647, 905',
  LandingSignupEmail: 'Business Email',
  LandingSignupEmailPlaceholder: 'john@smithroofing.com',
  LandingSignupTrade: 'Your Trade',
  LandingSignupTradeSelect: 'Select trade...',
  LandingSignupPlan: 'Select Plan',
  LandingSignupPlanSep: ' — ',
  LandingSignupSubmit: 'Complete Step 1 — Choose Your Number Next',
  LandingSignupGuarantee: 'Secure checkout · 14-day free trial · No contracts',
  LandingSignupErrorRequired: 'Please fill in all fields before continuing.',
  LandingSignupErrorEmail: 'Please enter a valid email address.',
  LandingSignupErrorPhone: 'Please enter a valid phone number in international format, e.g. +14155550123.',
  LandingSignupSuccessTitle: 'Step 1 Complete.',
  LandingSignupSuccessStep1: 'Account details received',
  LandingSignupSuccessSetPassword: 'Set your password',
  LandingSignupSuccessStep2: 'Choose your dedicated Echo number',
  LandingSignupSuccessStep3: 'Upload your knowledge base & go live instantly!',
  LandingSignupSuccessCTA: 'Go To Step 2 →',
  // Footer
  LandingFooterTagline: 'A SKAR AI Product — Built for Trades. Powered by AI.',
  FooterCompanyAlt: 'SKAR AI company mark',
  FooterCompanyName: 'SKAR AI',
  FooterCompanyHQ: 'Vancouver, BC. Canada.',
  FooterCompanyLegal: '1594598 B.C. LTD.',
  FooterQuickLinksTitle: 'Quick Links',
  FooterQuickLinkHome: 'Home',
  FooterQuickLinkDashboard: 'Dashboard',
  FooterQuickLinkDemo: 'Live Demo',
  FooterQuickLinkCompany: 'Company',
  FooterQuickLinkTerms: 'Terms of Service',
  FooterQuickLinkPrivacy: 'Privacy Policy',
  FooterQuickLinkSMS: 'SMS Notice',
  FooterQuickLinkHelp: 'More Help',
  FooterDemoPhoneFallback: 'Demo currently unavailable.',
  FooterCopyrightPrefix: 'ⓒ',
  FooterCopyrightSuffix: 'All Rights Reserved.',

  // Language choices. Keyed by the raw locale code so the selector can resolve a display name from any
  // active/selectable code (e.g. Localized[selectedLang]). Single source in @shared/common GetLocaleData
  // so the lobby and portal stay identical; these values are translated along with the rest of the
  // dictionary, so the selector shows language names in the currently active language.
  ...LocaleLabels,
  // About page (public info site).
  // Header / nav
  AboutNavDemo: 'Demo',
  AboutNavCompany: 'Company',
  AboutNavTerms: 'Terms',
  AboutNavHelp: 'Help',
  AboutNavBack: 'Get Started',
  // Demo tab
  AboutDemoLabel: 'Product Demo',
  AboutDemoTitle: 'See Echo In Action',
  AboutDemoWatchLabel: 'Watch',
  AboutDemoVideoFallback: 'Your browser does not support the video tag.',
  AboutDemoListenLabel: 'Listen',
  AboutDemoListenTitle: 'Hear Echo Handle Calls',
  AboutDemoAudioFallback: 'Your browser does not support the audio tag.',
  AboutDemoLiveLabel: 'Live',
  AboutDemoLiveTitle: 'Call or Text Echo Live Now',
  AboutDemoLivePhotoAlt: 'Echo AI Receptionist',
  AboutDemoUnavailable: 'Demo currently unavailable, check back soon.',
  AboutDemoLiveSubtitle:
    "You'll be speaking with the AI Receptionist for Vertex Echo itself. Ask it anything. What Vertex Echo does, how setup works, features, pricing, or how it would handle calls for your business. It answers naturally from real product knowledge, can book you a free live demo meeting right on the call, and is honest about being an AI. It's the same experience your callers would get, day or night.",
  AboutDemoListenSubtitle: 'This recording is from an actual call to the Vertex Echo AI Receptionist for a roofing business. Names, locations, and voice tones have been altered for privacy.',
  // Company tab
  AboutCompanyLabel: 'Company',
  AboutCompanyTitle: 'About SKAR AI',
  AboutCompanyIntro:
    'SKAR AI is the developer of Vertex Echo, an AI voice receptionist platform built for small businesses. We combine advanced conversational AI with real-world automation to ensure every missed call becomes a booked job. Our mission is to level the playing field for trades and service businesses by making enterprise-grade phone automation accessible, affordable, and effortless.',
  AboutCompanyHQ: 'Headquarters',
  AboutCompanyHQValue: 'Vancouver, BC. Canada',
  AboutCompanyContact: 'Contact',
  AboutCompanyFoundersLabel: 'Leadership',
  AboutCompanyFoundersTitle: 'Meet The Founders',
  AboutFounderCTOTitle: 'Chief Technology Officer',
  AboutFounderCTOBio:
    '{0} brings extensive experience in software engineering, automation, and AI systems. He has architected large-scale distributed platforms and machine-learning pipelines, and leads Vertex Echo\u2019s technical vision — from voice synthesis to real-time call orchestration.',
  AboutFounderCPOTitle: 'Chief Product Officer',
  AboutFounderCPOBio:
    '{0} combines deep expertise in product design, customer service, and marketing. He shapes Vertex Echo\u2019s product strategy, user experience, and go-to-market motion, ensuring the platform solves real problems for real businesses.',
  AboutLinkedInLabel: 'LinkedIn',
  // Terms tab (reuses AuthTerms* keys for body copy)
  AboutTermsLabel: 'Terms',
  AboutTermsTitle: 'Legal',
  AboutTermsToSPanelTitle: 'Terms of Service',
  AboutTermsSMSPanelTitle: 'SMS Notice',
  // Help / FAQ tab
  AboutHelpLabel: 'Help',
  AboutHelpTitle: 'Frequently Asked Questions',
  AboutHelpForwardingQ: 'How do I forward calls to my Echo number?',
  AboutHelpForwardingA:
    'Call forwarding sends incoming calls from your existing business line to your dedicated Echo AI Number. There are two modes: {3}Conditional{3} forwarding hands a call to Echo only when your ' +
    'line goes unanswered (recommended - you answer what you can, Echo catches the rest), while {3}Always{3} forwarding sends every call straight to Echo. Some carriers also offer a separate ' +
    '{3}Busy{3} setting that forwards only while you are on another call. The exact dial codes depend on your carrier.\n\n' +
    '{0}United States — Verizon{0}\n' +
    '{2}Conditional - only when the call goes unanswered or your line is busy (combined on Verizon):{2}\n' +
    '1. Dial {1}*71{1} followed by your Echo number, then press Call.\n' +
    '2. Wait for the confirmation tone or message.\n' +
    '3. To turn off, dial {1}*73{1}.\n' +
    '{2}Always - every call, immediately:{2}\n' +
    '1. Dial {1}*72{1} followed by your Echo number, then press Call.\n' +
    '2. Wait for the confirmation tone or message.\n' +
    '3. To turn off, dial {1}*73{1}.\n\n' +
    '{0}United States — AT&T{0}\n' +
    '{2}Conditional - only when the call goes unanswered:{2}\n' +
    '1. Dial {1}**61*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. Wait for the confirmation message.\n' +
    '3. To turn off, dial {1}##61#{1} and press Call.\n' +
    '{2}Busy - only while you are on another call:{2}\n' +
    '1. Dial {1}**67*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##67#{1} and press Call.\n' +
    '{2}Always - every call, immediately:{2}\n' +
    '1. Dial {1}**21*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##21#{1} and press Call.\n\n' +
    '{0}United States — T-Mobile{0}\n' +
    '{2}Conditional - only when the call goes unanswered:{2}\n' +
    '1. Dial {1}**61*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##61#{1} and press Call.\n' +
    '{2}Busy - only while you are on another call:{2}\n' +
    '1. Dial {1}**67*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##67#{1} and press Call.\n' +
    '{2}Always - every call, immediately:{2}\n' +
    '1. Dial {1}**21*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##21#{1} and press Call.\n\n' +
    '{0}Canada — Rogers{0}\n' +
    '{2}Conditional - only when the call goes unanswered:{2}\n' +
    '1. Dial {1}**61*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##61#{1} and press Call.\n' +
    '{2}Busy - only while you are on another call:{2}\n' +
    '1. Dial {1}**67*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##67#{1} and press Call.\n' +
    '{2}Always - every call, immediately:{2}\n' +
    '1. Dial {1}**21*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##21#{1} and press Call.\n\n' +
    '{0}Canada — Bell{0}\n' +
    '{2}Conditional - only when the call goes unanswered:{2}\n' +
    '1. Dial {1}**61*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##61#{1} and press Call.\n' +
    '{2}Busy - only while you are on another call:{2}\n' +
    '1. Dial {1}**67*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##67#{1} and press Call.\n' +
    '{2}Always - every call, immediately:{2}\n' +
    '1. Dial {1}**21*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##21#{1} and press Call.\n\n' +
    '{0}Canada — Telus{0}\n' +
    '{2}Conditional - only when the call goes unanswered:{2}\n' +
    '1. Dial {1}**61*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##61#{1} and press Call.\n' +
    '{2}Busy - only while you are on another call:{2}\n' +
    '1. Dial {1}**67*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##67#{1} and press Call.\n' +
    '{2}Always - every call, immediately:{2}\n' +
    '1. Dial {1}**21*{1} followed by your Echo number, then {1}#{1}, and press Call.\n' +
    '2. To turn off, dial {1}##21#{1} and press Call.\n\n' +
    'Tip: on most carriers {1}##002#{1} turns off every kind of forwarding at once. Traditional landlines usually use {1}*72{1} to enable Always forwarding and {1}*73{1} to disable it - check with ' +
    'your provider. If a code does not work, your carrier can enable forwarding for you in a minute over the phone.\n\n' +
    'Note: forwarding is optional but recommended. You can use your Echo number as your main business line, or keep your current number and forward only during off-hours, busy periods, or permanently — whatever fits your workflow.',
  AboutHelpNumberQ: 'Can I use my Echo number as my main business line?',
  AboutHelpNumberA:
    'Yes. Your Echo number is a real phone number that can receive and place calls. Many businesses choose to publish it as their primary line. Others prefer to keep their existing number for outbound calls and forward inbound calls to Echo. The choice is yours.',
  AboutHelpTrialQ: 'Is there a free trial?',
  AboutHelpTrialA:
    'Yes. Every plan includes a 14-day free trial with full access to all features. We collect a card at signup to verify your business, but you are not charged until the trial ends, and you can cancel anytime before then.',
  AboutHelpCancelQ: 'How do I cancel?',
  AboutHelpCancelA:
    'You can cancel your subscription at any time from the Billing page in your dashboard. There are no contracts or cancellation fees. Your service continues until the end of your current billing period.',
  AboutHelpLanguagesQ: 'What languages does Echo support?',
  AboutHelpLanguagesA:
    'Echo currently supports English, Spanish, French, German, Italian, Dutch, Portuguese, and many more languages. You can set your preferred language during onboarding and change it anytime in your dashboard. Echo also follows the caller\u2019s lead: if someone switches to another supported language mid-call, Echo switches with them and keeps the conversation flowing naturally.',
  AboutHelpAICustomQ: 'Can I customize what Echo says to callers?',
  AboutHelpAICustomA:
    'Absolutely. You control Echo\u2019s greeting, the questions it asks, the information it shares, and how it handles different call types. Upload your business details, set your availability, and define escalation rules — Echo adapts to your business, not the other way around.',
  AboutHelpPricingQ: 'How much does Echo cost?',
  AboutHelpPricingA:
    'Plans are sized for every call volume, and every plan includes a 14-day free trial with full access to all features. See the Pricing section of our website for current rates and allowances. Need something bigger or different? Custom plans with higher allowances and more simultaneous lines are available on request - contact us at {0} and we will put a package together.',
  AboutHelpConcurrentQ: 'What happens if several calls come in at once?',
  AboutHelpConcurrentA:
    'Your plan includes a set number of simultaneous lines, so Echo answers overlapping calls at the same time. If every line is busy, the caller hears a polite message asking them to try again shortly, and you are notified right away - no lead slips away quietly. Need more simultaneous lines? Custom plans with higher line counts are available - contact us at {0}.',
  AboutHelpAITransparencyQ: 'Will callers know they are talking to an AI?',
  AboutHelpAITransparencyA:
    'Echo always lets callers know the call is recorded at the top of the call, and if a caller asks whether they are speaking with an AI, it answers honestly and keeps helping. No pretending, no awkward surprises.',
  AboutHelpUnknownQ: 'What if Echo does not know an answer?',
  AboutHelpUnknownA: 'It never guesses. Echo takes a detailed message, sends you an instant summary by text or email, and can escalate urgent calls to you right away based on the rules you set.',
  AboutHelpBookingQ: 'Can Echo book appointments for me?',
  AboutHelpBookingA: 'Yes. Connect your Google Calendar or Outlook, and Echo checks your real availability and books the appointment during the call - confirmed to you and the caller right away.',
  AboutHelpTextingQ: 'Can my customers text instead of calling?',
  AboutHelpTextingA:
    'Yes. After every answered call, the caller gets a short text from your Echo number inviting them to continue by text - and from there they can ask questions, check or confirm their appointment, or book a new one, all with the same assistant. Customers can also text your Echo number first, without ever calling. Every thread is on your Texts dashboard page, where you can read it and jump in yourself at any time. Customers can reply STOP to pause texts and START to resume.'
};

/** To be used as the original untranslated localization object. */
export const LocalizedOriginal = { ...Localized } as const;

/** The initial language code. */
let currentLanguage: LocalizationCode = CommonConstants.Defaults.Localization;

/** @returns The original (untranslated) language code. */
export function GetOriginalLanguage(): LocalizationCode {
  return CommonConstants.Defaults.Localization;
}

/** @returns The current language code. */
export function GetCurrentLanguage(): LocalizationCode {
  return currentLanguage;
}

/**
 * @param localizationObject - The translated localization object.
 * @param localizationObject.source
 * @param localizationObject.target
 * @param localizationObject.content
 */
export function SetTranslatedLocalizationObject(localizationObject: { source: string; target: string; content: Record<string, string> }) {
  const newLanguage = localizationObject.target as LocalizationCode;
  const translatedContent = localizationObject.content;

  currentLanguage = newLanguage;
  SetStoredLanguage(newLanguage);
  // Reset-first: every key falls back to its original (untranslated) value before the payload is applied. A partial payload (missing keys, e.g. a different target language whose batch
  // omitted some entries) must NEVER leave a previous language's value in place — that produces a mixed-language dictionary even though langVersion bumps. Any key the payload does not
  // translate reverts to the source copy rather than retaining stale text.
  const originals = LocalizedOriginal as Record<string, string>;
  for (const key of Object.keys(Localized)) {
    Localized[key] = translatedContent[key] || originals[key];
  }
}
